import { useQuery } from "@tanstack/react-query";
import { Newspaper, Languages, FileText, CheckCircle, Clock } from "lucide-react";

interface AssistantStats {
  blog: { total: number; draft: number; published: number };
  translations: { totalKeys: number };
}

async function fetchAssistantStats(): Promise<AssistantStats> {
  const res = await fetch("/api/assistant/stats", { credentials: "include" });
  if (!res.ok) throw new Error("Failed to fetch stats");
  return res.json();
}

function StatCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: typeof Newspaper;
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 flex items-center gap-4 shadow-sm">
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${color}`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-sm text-slate-500">{label}</p>
      </div>
    </div>
  );
}

export default function AssistantDashboard() {
  const { data, isLoading, isError } = useQuery<AssistantStats>({
    queryKey: ["/api/assistant/stats"],
    queryFn: fetchAssistantStats,
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-slate-500">
        Failed to load stats. Please refresh.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Overview of your content and translations</p>
      </div>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Blog</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard icon={Newspaper} label="Total posts" value={data.blog.total} color="bg-violet-500" />
          <StatCard icon={CheckCircle} label="Published" value={data.blog.published} color="bg-emerald-500" />
          <StatCard icon={Clock} label="Drafts" value={data.blog.draft} color="bg-amber-400" />
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Translations</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <StatCard icon={Languages} label="Total translation keys" value={data.translations.totalKeys} color="bg-blue-500" />
          <StatCard icon={FileText} label="Languages" value={3} color="bg-indigo-500" />
        </div>
      </section>
    </div>
  );
}
